import { supabase } from "@/integrations/supabase/client";
import type { AdminUserRow, AppUser, AuditLog, Project, ProjectStatus, Report } from "./types";

// ---- Reports ----
export async function getReports(): Promise<Report[]> {
  const { data, error } = await supabase
    .from("reports")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) { console.error(error); return []; }
  return (data ?? []) as unknown as Report[];
}

export async function saveReport(input: Omit<Report, "id" | "created_at">): Promise<Report | null> {
  const { data, error } = await supabase
    .from("reports")
    .insert(input as never)
    .select()
    .single();
  if (error) { console.error(error); return null; }
  return data as unknown as Report;
}

export async function deleteReport(id: string) {
  const { error } = await supabase.from("reports").delete().eq("id", id);
  if (error) console.error(error);
}

export async function getReport(id: string): Promise<Report | null> {
  const { data, error } = await supabase.from("reports").select("*").eq("id", id).maybeSingle();
  if (error) { console.error(error); return null; }
  return (data as unknown as Report) ?? null;
}

// ---- Projects ----
export async function getProjects(): Promise<Project[]> {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) { console.error(error); return []; }
  return (data ?? []) as unknown as Project[];
}

export async function createProject(input: { title: string; description: string; department: string; owner_id: string }): Promise<Project | null> {
  const { data, error } = await supabase
    .from("projects")
    .insert({ ...input, status: "backlog" } as never)
    .select()
    .single();
  if (error) { console.error(error); return null; }
  return data as unknown as Project;
}

export async function updateProjectStatus(id: string, status: ProjectStatus) {
  const { error } = await supabase.from("projects").update({ status } as never).eq("id", id);
  if (error) console.error(error);
}

export async function deleteProject(id: string) {
  const { error } = await supabase.from("projects").delete().eq("id", id);
  if (error) console.error(error);
}

// ---- Audit ----
export async function getAudit(): Promise<AuditLog[]> {
  const { data, error } = await supabase
    .from("audit_logs")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) { console.error(error); return []; }
  return (data ?? []) as AuditLog[];
}

export async function logAudit(user: AppUser | null, action: string, target?: string) {
  if (!user) return;
  const { error } = await supabase.rpc("log_audit_event", { _action: action, _target: target ?? null });
  if (error) console.error(error);
}

// ---- Admin: users ----
export async function getAdminUsers(): Promise<AdminUserRow[]> {
  const [{ data: profiles, error: pErr }, { data: roles, error: rErr }] = await Promise.all([
    supabase.from("profiles").select("id, email, name, created_at"),
    supabase.from("user_roles").select("user_id, role"),
  ]);
  if (pErr) console.error(pErr);
  if (rErr) console.error(rErr);
  const roleMap = new Map<string, "admin" | "member">();
  (roles ?? []).forEach((r) => {
    const existing = roleMap.get(r.user_id);
    // admin wins
    if (r.role === "admin" || !existing) roleMap.set(r.user_id, r.role);
  });
  return (profiles ?? []).map((p) => ({
    id: p.id,
    email: p.email,
    name: p.name,
    created_at: p.created_at,
    role: roleMap.get(p.id) ?? "member",
  }));
}

export async function setUserRole(userId: string, role: "admin" | "member") {
  // Remove existing roles then insert the new one
  await supabase.from("user_roles").delete().eq("user_id", userId);
  const { error } = await supabase.from("user_roles").insert({ user_id: userId, role } as never);
  if (error) console.error(error);
}
